import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SetHomepageButton from '../SetHomepageButton/SetHomepageButton';
import { reverbAffiliateUrl } from '../../utils/affiliateLinks';
import './ValuationPortalScreen.css';

const API_URL = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const CONDITIONS = ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor', 'Project/Parts'];

function formatCategory(cat) {
  const labels = {
    original_vintage: 'Original Vintage',
    reissue: 'Reissue',
    boutique: 'Boutique',
    replica_clone: 'Replica / Clone',
    custom_build: 'Custom Build',
    modern_production: 'Modern Production',
  };
  return labels[cat] || (cat ? cat.replace(/_/g, ' ') : '');
}

function ValuationPortalScreen() {
  const { isAuthenticated, user, token } = useAuth();
  const [searchParams] = useSearchParams();
  // Capture the session_id at mount time — only relevant on the Stripe redirect
  const initialSessionId = React.useRef(searchParams.get('session_id'));
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [condition, setCondition] = useState('');
  // step: 'form' | 'loading' | 'preview' | 'checkout_loading' | 'confirmation' | 'result'
  const [step, setStep] = useState('form');
  const [previewData, setPreviewData] = useState(null);
  const [result, setResult] = useState(null);
  const [referenceCode, setReferenceCode] = useState(null);
  const [error, setError] = useState('');
  const [isPaidResult, setIsPaidResult] = useState(false);

  const isPro = user?.subscription_tier === 'pro'
    || user?.subscription_tier === 'business'
    || user?.is_admin;

  // On mount: if Stripe redirected back with ?session_id=xxx, auto-fetch the result
  useEffect(() => {
    const sessionId = initialSessionId.current;
    if (!sessionId) return;

    setStep('loading');
    setError('');
    fetch(`${API_URL}/public/valuate/from-payment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stripe_session_id: sessionId }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (res.ok) {
          setResult(data);
          setIsPaidResult(true);
          if (data.reference_code) {
            setReferenceCode(data.reference_code);
            setStep('confirmation');
          } else {
            setStep('result');
          }
          window.history.replaceState({}, '', '/value');
        } else {
          setError(data.error || 'Failed to retrieve valuation result.');
          setStep('form');
        }
      })
      .catch(() => { setError('Network error. Please try again.'); setStep('form'); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setResult(null);
    setPreviewData(null);
    setIsPaidResult(false);
    setStep('loading');

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${API_URL}/public/valuate/preview`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          make,
          model,
          year: year ? parseInt(year, 10) : null,
          condition: condition || null,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Valuation failed. Please try again.');
        setStep('form');
      } else if (data.free_for_pro) {
        // Pro/Admin: full result returned directly — no charge
        setResult(data);
        setIsPaidResult(true);
        setStep('result');
      } else {
        // Non-Pro: show preview/confirmation before payment
        setPreviewData(data);
        setStep('preview');
      }
    } catch {
      setError('Network error. Please try again.');
      setStep('form');
    }
  }

  async function handleCheckout() {
    setStep('checkout_loading');
    setError('');
    try {
      const res = await fetch(`${API_URL}/public/valuate/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preview_id: previewData.preview_id }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to start payment. Please try again.');
        setStep('preview');
      }
    } catch {
      setError('Network error. Please try again.');
      setStep('preview');
    }
  }

  function formatCurrency(n) {
    if (!n) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(n);
  }

  function escHtml(s) {
    return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function handlePrint() {
    const ampName = [make, model].filter(Boolean).join(' ');
    const meta = [year && `Year: ${year}`, condition && `Condition: ${condition}`].filter(Boolean).join(' · ');
    const issueDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const certId = 'AV-' + new Date().toISOString().slice(0, 10).replace(/-/g, '') + '-' + Math.random().toString(36).slice(-4).toUpperCase();
    const categoryLabel = formatCategory(result.category);
    const fmt = formatCurrency;
    const html = `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><title>AmpNebula Certificate of Valuation — ${escHtml(ampName)}</title>
<style>
  @page { size: letter; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: Georgia, serif; background: #fff; color: #111;
    padding: 0.6in 0.72in; font-size: 13px;
  }
  body::before {
    content: '';
    position: fixed;
    top: 0.22in; left: 0.22in; right: 0.22in; bottom: 0.22in;
    border: 14px double #1e3a8a;
    pointer-events: none;
  }
  .cert-seal {
    width: 68px; height: 68px; border-radius: 50%;
    border: 2.5px solid #1e3a8a;
    outline: 2px solid #93c5fd; outline-offset: 3px;
    background: radial-gradient(circle at 35% 30%, #f0f9ff 0%, #dbeafe 65%, #bfdbfe 100%);
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    font-size: 8px; font-weight: 900; text-transform: uppercase; letter-spacing: 0.07em;
    color: #1e3a8a; text-align: center; font-family: Arial, sans-serif; line-height: 1.35;
    flex-shrink: 0;
  }
  .cert-header { display: flex; align-items: center; gap: 1rem; padding-bottom: 0.85rem; }
  .cert-header-center { flex: 1; text-align: center; }
  .cert-brand { font-family: Arial, sans-serif; font-size: 1.55rem; font-weight: 900; color: #1e3a8a; letter-spacing: -0.02em; }
  .cert-doc-title { font-family: Arial, sans-serif; font-size: 0.58rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.22em; color: #64748b; margin-top: 0.18rem; }
  .cert-header-right { text-align: right; min-width: 68px; }
  .cert-ref { font-family: 'Courier New', monospace; font-size: 0.7rem; color: #475569; font-weight: 600; }
  .cert-date { font-family: Arial, sans-serif; font-size: 0.72rem; color: #94a3b8; margin-top: 0.2rem; }
  .cert-rule { display: flex; align-items: center; gap: 0.5rem; margin: 0.8rem 0; font-size: 0.82rem; color: #2563eb; }
  .cert-rule::before, .cert-rule::after { content: ''; flex: 1; height: 1px; background: linear-gradient(90deg, transparent, #2563eb 30%, #1e40af 50%, #2563eb 70%, transparent); }
  .cert-subject { margin: 0.65rem 0; padding: 0.9rem 1.2rem; background: linear-gradient(105deg, #f8fafc 0%, #eff6ff 100%); border: 1px solid #e2e8f0; border-left: 4px solid #1e40af; border-radius: 0 8px 8px 0; }
  .cert-subject h1 { font-size: 1.5rem; color: #0f172a; font-weight: 700; }
  .cert-subject .meta { font-family: Arial, sans-serif; font-size: 0.82rem; color: #64748b; margin-top: 0.25rem; }
  .cert-category { display: inline-block; margin-top: 0.35rem; font-family: Arial, sans-serif; font-size: 0.6rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; background: #dbeafe; color: #1e40af; border: 1px solid #bfdbfe; border-radius: 4px; padding: 0.15rem 0.55rem; }
  .cert-fmv { text-align: center; margin: 0.85rem 0; padding: 1.1rem 1.5rem; background: linear-gradient(135deg, #eff6ff 0%, #e0f2fe 50%, #eff6ff 100%); border: 1px solid #bfdbfe; border-radius: 8px; position: relative; overflow: hidden; }
  .cert-fmv::before { content: ''; position: absolute; inset: 4px; border: 1px solid rgba(147, 197, 253, 0.5); border-radius: 5px; pointer-events: none; }
  .cert-fmv-label { font-family: Arial, sans-serif; font-size: 0.58rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.18em; color: #2563eb; display: block; margin-bottom: 0.3rem; }
  .cert-fmv-amount { font-size: 2.6rem; font-weight: 800; color: #1e3a8a; line-height: 1.1; display: block; letter-spacing: -0.01em; }
  .cert-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.65rem; margin: 0.85rem 0; }
  .cert-grid-item { text-align: center; padding: 0.65rem 0.5rem; border: 1px solid #e2e8f0; border-radius: 6px; background: #fafafa; }
  .cert-grid-label { font-family: Arial, sans-serif; font-size: 0.58rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #94a3b8; display: block; margin-bottom: 0.25rem; }
  .cert-grid-value { font-size: 0.95rem; font-weight: 700; color: #1e293b; }
  .cert-section { margin: 0.9rem 0; }
  .cert-section h3 { font-family: Arial, sans-serif; font-size: 0.6rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: #94a3b8; margin-bottom: 0.35rem; padding-bottom: 0.2rem; border-bottom: 1px solid #f1f5f9; }
  .cert-section p { font-size: 0.85rem; color: #334155; line-height: 1.65; }
  .cert-list { margin: 0; padding-left: 1.25rem; font-size: 0.85rem; color: #334155; line-height: 1.65; list-style: disc; }
  .cert-attestation { margin: 1rem 0 0.75rem; padding: 0.75rem 1.1rem; border-top: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; }
  .cert-attestation p { font-style: italic; font-size: 0.78rem; color: #64748b; line-height: 1.65; text-align: center; }
  .cert-footer { margin-top: 0.9rem; display: flex; justify-content: space-between; align-items: flex-end; gap: 1rem; }
  .cert-footer-brand { font-family: Arial, sans-serif; font-size: 0.8rem; font-weight: 900; color: #1e3a8a; }
  .cert-footer-sub { font-family: Arial, sans-serif; font-size: 0.6rem; color: #94a3b8; margin-top: 0.1rem; }
  .cert-disclaimer { font-family: Arial, sans-serif; font-size: 0.6rem; color: #94a3b8; max-width: 340px; text-align: right; line-height: 1.5; }

</style></head>
<body>
  <div class="cert-header">
    <div class="cert-seal">AMP<br>NEBULA</div>
    <div class="cert-header-center">
      <div class="cert-brand">AmpNebula™</div>
      <div class="cert-doc-title">Certificate of Valuation</div>
    </div>
    <div class="cert-header-right">
      <div class="cert-ref">${escHtml(certId)}</div>
      <div class="cert-date">${escHtml(issueDate)}</div>
    </div>
  </div>
  <div class="cert-rule"><span>✦</span></div>
  <div class="cert-subject">
    <h1>${escHtml(ampName)}</h1>
    ${meta ? `<div class="meta">${escHtml(meta)}</div>` : ''}
    ${categoryLabel ? `<div class="cert-category">${escHtml(categoryLabel)}</div>` : ''}
  </div>
  <div class="cert-fmv">
    <span class="cert-fmv-label">Appraised Fair Market Value</span>
    <span class="cert-fmv-amount">${escHtml(fmt(result.estimated_value))}</span>
  </div>
  <div class="cert-grid">
    <div class="cert-grid-item"><span class="cert-grid-label">Low</span><span class="cert-grid-value">${escHtml(fmt(result.estimated_value_low))}</span></div>
    <div class="cert-grid-item"><span class="cert-grid-label">High</span><span class="cert-grid-value">${escHtml(fmt(result.estimated_value_high))}</span></div>
    <div class="cert-grid-item"><span class="cert-grid-label">Trade-in Value</span><span class="cert-grid-value">${escHtml(fmt(result.trade_in_value))}</span></div>
    <div class="cert-grid-item"><span class="cert-grid-label">Insurance / Replacement</span><span class="cert-grid-value">${escHtml(fmt(result.insurance_value))}</span></div>
  </div>
  <div class="cert-rule"><span>✦</span></div>
  ${result.reasoning ? `<div class="cert-section"><h3>Analysis</h3><p>${escHtml(result.reasoning)}</p></div>` : ''}
  ${result.reverb_price_guide ? `<div class="cert-section"><h3>Reverb Price Guide</h3><p>${escHtml(result.reverb_price_guide.title)} &mdash; ${escHtml(fmt(result.reverb_price_guide.estimatedLow))} &ndash; ${escHtml(fmt(result.reverb_price_guide.estimatedHigh))}</p></div>` : ''}
  ${result.comparable_sales ? `<div class="cert-section"><h3>Comparable Sales</h3><ul class="cert-list">${(Array.isArray(result.comparable_sales) ? result.comparable_sales : result.comparable_sales.split('\n')).filter(l => l.trim()).map(l => `<li>${escHtml(l.trim())}</li>`).join('')}</ul></div>` : ''}
  ${result.market_factors ? `<div class="cert-section"><h3>Market Context</h3><p>${escHtml(result.market_factors)}</p></div>` : ''}
  <div class="cert-attestation">
    <p>This certificate confirms that the above instrument has been appraised by the AmpNebula AI Valuation System using live market data, comparable sales research, and independent price guide analysis as of the date of issue. Values represent evidence-based estimates and are subject to prevailing market conditions.</p>
  </div>
  <div class="cert-footer">
    <div>
      <div class="cert-footer-brand">ampnebula.com</div>
      <div class="cert-footer-sub">AI-Powered Amplifier Valuation</div>
    </div>
    <div class="cert-disclaimer">For informational purposes only. This document does not constitute a guarantee of purchase, sale, or insurance value. Ref: ${escHtml(certId)}</div>
  </div>
  <script>window.onload = function() { setTimeout(function() { window.print(); }, 250); }${'<'}/script>
</body></html>`;
    const win = window.open('', '_blank', 'width=900,height=700');
    if (!win) {
      alert('Please allow popups for this site to print the valuation report.');
      return;
    }
    win.document.write(html);
    win.document.close();
  }

  return (
    <div className="valuation-portal">
      <div className="valuation-portal__hero">
        <div className="valuation-portal__hero-content">
          <h1>What's My Amp Worth?</h1>
          <p className="valuation-portal__subtitle">
            AmpNebula Valuation™ — AI-powered market analysis with real-time Reverb data
          </p>
          <div style={{ marginTop: '8px' }}><SetHomepageButton pagePath="/value" /></div>
          {isPro && (
            <span className="valuation-portal__badge valuation-portal__badge--pro">
              Pro — Unlimited Valuations
            </span>
          )}
          {!isPro && (
            <span className="valuation-portal__badge valuation-portal__badge--free">
              $1.99 per valuation ·{' '}
              <Link
                to={isAuthenticated ? '/profile' : '/signup'}
                className="valuation-portal__inline-link"
              >
                Upgrade to Pro
              </Link>
              {' '}for unlimited
            </span>
          )}
        </div>
      </div>

      <div className="valuation-portal__body">

        {/* ── Step 1 & 2: Form + loading ───────────────────────────────────── */}
        {(step === 'form' || step === 'loading') && (
          <div className="valuation-portal__card">
            <form onSubmit={handleSubmit} className="valuation-portal__form">
              <div className="valuation-portal__field">
                <label htmlFor="vp-make">Manufacturer *</label>
                <input
                  id="vp-make"
                  type="text"
                  value={make}
                  onChange={e => setMake(e.target.value)}
                  placeholder="e.g. Fender, Marshall, Mesa Boogie"
                  required
                  disabled={step === 'loading'}
                />
              </div>
              <div className="valuation-portal__field">
                <label htmlFor="vp-model">Model *</label>
                <input
                  id="vp-model"
                  type="text"
                  value={model}
                  onChange={e => setModel(e.target.value)}
                  placeholder="e.g. Deluxe Reverb, JCM800, Rectifier"
                  required
                  disabled={step === 'loading'}
                />
              </div>
              <div className="valuation-portal__row">
                <div className="valuation-portal__field">
                  <label htmlFor="vp-year">
                    Year <span className="valuation-portal__optional">(optional)</span>
                  </label>
                  <input
                    id="vp-year"
                    type="number"
                    value={year}
                    onChange={e => setYear(e.target.value)}
                    placeholder="e.g. 1965"
                    min="1900"
                    max={new Date().getFullYear() + 1}
                    disabled={step === 'loading'}
                  />
                </div>
                <div className="valuation-portal__field">
                  <label htmlFor="vp-condition">
                    Condition <span className="valuation-portal__optional">(optional)</span>
                  </label>
                  <select
                    id="vp-condition"
                    value={condition}
                    onChange={e => setCondition(e.target.value)}
                    disabled={step === 'loading'}
                  >
                    <option value="">Select condition…</option>
                    {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              {error && <div className="valuation-portal__error" role="alert">{error}</div>}

              <button
                type="submit"
                className="valuation-portal__btn valuation-portal__btn--primary"
                disabled={step === 'loading'}
              >
                {step === 'loading' ? 'Analyzing Market…' : 'Analyze My Amp'}
              </button>
            </form>

            {step === 'loading' && (
              <div className="valuation-portal__loading">
                <div className="valuation-portal__spinner" aria-label="Loading" />
                <p>Researching market data and comparable sales…</p>
              </div>
            )}
          </div>
        )}

        {/* ── Step 3: Preview / confirmation ───────────────────────────────── */}
        {(step === 'preview' || step === 'checkout_loading') && previewData && (
          <div className="valuation-portal__preview-card">
            <button
              type="button"
              className="valuation-portal__btn valuation-portal__btn--back"
              onClick={() => { setStep('form'); setPreviewData(null); setError(''); }}
            >
              ← Change Amp
            </button>

            <p className="valuation-portal__preview-amp">
              {previewData.make} {previewData.model}
              {(year || condition) && (
                <span className="valuation-portal__preview-meta">
                  {[year, condition].filter(Boolean).join(' · ')}
                </span>
              )}
            </p>

            {previewData.confidence === 'high' ? (
              <div className="valuation-portal__confidence-gate">
                <span className="valuation-portal__confidence valuation-portal__confidence--high">
                  HIGH CONFIDENCE
                </span>
                <h2>We found strong market data for your {previewData.make} {previewData.model}</h2>
                <p className="valuation-portal__gate-desc">
                  Pay $1.99 to see your complete valuation — estimated fair market value,
                  trade-in value, insurance/replacement value, and comparable sales.
                </p>
              </div>
            ) : (
              <div className="valuation-portal__confidence-gate">
                <span className={`valuation-portal__confidence valuation-portal__confidence--${previewData.confidence}`}>
                  {previewData.confidence?.toUpperCase()} CONFIDENCE
                </span>
                <h2>We found some comparables — do these match your amp?</h2>
                <p className="valuation-portal__gate-desc">
                  We couldn't find an exact match in our market data. These are the closest
                  comparables we found. If they're similar to your amp, proceed and we'll
                  give you our best estimate for $1.99.
                </p>
                {previewData.comparables_preview && (
                  <div className="valuation-portal__comparables-section">
                    <h3>Comparables found:</h3>
                    <p>{previewData.comparables_preview}</p>
                  </div>
                )}
                {previewData.market_context && (
                  <p className="valuation-portal__market-context">{previewData.market_context}</p>
                )}
              </div>
            )}

            <div className="valuation-portal__gate-actions">
              {step === 'checkout_loading' ? (
                <div className="valuation-portal__loading valuation-portal__loading--inline">
                  <div className="valuation-portal__spinner" aria-label="Loading" />
                  <p>Redirecting to payment…</p>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleCheckout}
                    className="valuation-portal__btn valuation-portal__btn--pay"
                  >
                    {previewData.confidence === 'high'
                      ? 'Pay $1.99 — Get Full Valuation'
                      : 'Yes, these match — Pay $1.99'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setStep('form'); setPreviewData(null); setError(''); }}
                    className="valuation-portal__btn valuation-portal__btn--ghost"
                  >
                    {previewData.confidence === 'high' ? 'Cancel' : 'No, start over'}
                  </button>
                </>
              )}
            </div>

            {error && <div className="valuation-portal__error" role="alert">{error}</div>}

            <div className="valuation-portal__upsell-card">
              <h3>Get 3 valuations for the price of 1</h3>
              <p>AmpNebula Pro — $1.99/month · Unlimited valuations · Full collection management</p>
              {isAuthenticated ? (
                <Link to="/profile" className="valuation-portal__btn valuation-portal__btn--upgrade">
                  Upgrade to Pro
                </Link>
              ) : (
                <Link to="/signup" className="valuation-portal__btn valuation-portal__btn--upgrade">
                  Create Account &amp; Upgrade to Pro
                </Link>
              )}
            </div>
          </div>
        )}

        {/* ── Step 3b: Confirmation (after Stripe payment) ────────────────── */}
        {step === 'confirmation' && result && (
          <div className="valuation-portal__card valuation-portal__confirmation">
            <div className="valuation-portal__confirmation-success">
              <span className="valuation-portal__confirmation-checkmark" aria-hidden="true">✓</span>
              <h2>Payment Confirmed</h2>
              <p className="valuation-portal__confirmation-sub">Your valuation report is ready.</p>
            </div>

            <div className="valuation-portal__confirmation-ref">
              <span className="valuation-portal__confirmation-ref-label">Reference Code</span>
              <span className="valuation-portal__confirmation-ref-code">{referenceCode}</span>
              <span className="valuation-portal__confirmation-ref-note">Save this code — you can use it to look up your valuation again later, or if you need support.</span>
            </div>

            <div className="valuation-portal__confirmation-details">
              <div>
                <span className="valuation-portal__value-label">Amp</span>
                <span>{[make, model, year].filter(Boolean).join(' ')}</span>
              </div>
              {condition && (
                <div>
                  <span className="valuation-portal__value-label">Condition</span>
                  <span>{condition}</span>
                </div>
              )}
              <div>
                <span className="valuation-portal__value-label">Value Range</span>
                <span>{formatCurrency(result.estimated_value_low)} – {formatCurrency(result.estimated_value_high)}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setStep('result')}
              className="valuation-portal__btn valuation-portal__btn--primary"
            >
              View Full Valuation Report
            </button>
          </div>
        )}

        {/* ── Step 4: Full result ───────────────────────────────────────────── */}
        {step === 'result' && result && (
          <div className="valuation-portal__result">
            <div className="valuation-portal__result-header">
              <div>
                <h2>AmpNebula Valuation™</h2>
                {(make || model) && (
                  <div className="valuation-portal__result-amp-name">
                    {[make, model].filter(Boolean).join(' ')}
                    {year && <span className="valuation-portal__result-amp-meta"> · {year}</span>}
                    {condition && <span className="valuation-portal__result-amp-meta"> · {condition}</span>}
                  </div>
                )}
                {result.category && (
                  <span className={`valuation-portal__category-badge valuation-portal__category-badge--${result.category}`}>
                    {formatCategory(result.category)}
                  </span>
                )}
              </div>
              <div className="valuation-portal__result-header-right">
                <span className={`valuation-portal__confidence valuation-portal__confidence--${result.confidence}`}>
                  {result.confidence?.toUpperCase()} CONFIDENCE
                </span>
                {isPaidResult && (
                  <button
                    type="button"
                    onClick={handlePrint}
                    className="valuation-portal__btn valuation-portal__btn--print"
                  >
                    🖨 Print Certificate
                  </button>
                )}
              </div>
            </div>

            <div className="valuation-portal__values">
              <div className="valuation-portal__value-primary">
                <span className="valuation-portal__value-label">Estimated Fair Market Value</span>
                <span className="valuation-portal__value-amount">
                  {formatCurrency(result.estimated_value)}
                </span>
              </div>
              <div className="valuation-portal__value-range">
                <div>
                  <span className="valuation-portal__value-label">Low</span>
                  <span>{formatCurrency(result.estimated_value_low)}</span>
                </div>
                <div>
                  <span className="valuation-portal__value-label">High</span>
                  <span>{formatCurrency(result.estimated_value_high)}</span>
                </div>
                <div>
                  <span className="valuation-portal__value-label">Trade-in Value</span>
                  <span>{formatCurrency(result.trade_in_value)}</span>
                </div>
                <div>
                  <span className="valuation-portal__value-label">Insurance/Replacement Value</span>
                  <span>{formatCurrency(result.insurance_value)}</span>
                </div>
              </div>
            </div>

            {result.reasoning && (
              <div className="valuation-portal__section">
                <h3>Analysis</h3>
                <p>{result.reasoning}</p>
              </div>
            )}
            {result.reverb_price_guide && (
              <div className="valuation-portal__section">
                <h3>Reverb Price Guide</h3>
                <p>
                  <a
                    href={reverbAffiliateUrl(result.reverb_price_guide.webUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="valuation-portal__inline-link"
                  >
                    {result.reverb_price_guide.title}
                  </a>
                  {' — '}
                  {formatCurrency(result.reverb_price_guide.estimatedLow)}
                  {' – '}
                  {formatCurrency(result.reverb_price_guide.estimatedHigh)}
                </p>
              </div>
            )}
            {result.comparable_sales && (
              <div className="valuation-portal__section">
                <h3>Comparable Sales</h3>
                <ul className="valuation-portal__comparables">
                  {(Array.isArray(result.comparable_sales) ? result.comparable_sales : result.comparable_sales.split('\n')).filter(l => l.trim()).map((line, i) => (
                    <li key={i}>{line.trim()}</li>
                  ))}
                </ul>
              </div>
            )}
            {result.market_factors && (
              <div className="valuation-portal__section">
                <h3>Market Context</h3>
                <p>{result.market_factors}</p>
              </div>
            )}

            {!isPro && (
              <div className="valuation-portal__upsell-card">
                <h3>Track your amp's value over time</h3>
                <p>Add it to your collection with Pro and watch market trends automatically.</p>
                {isAuthenticated ? (
                  <Link to="/profile" className="valuation-portal__btn valuation-portal__btn--upgrade">
                    Upgrade to Pro — $1.99/mo
                  </Link>
                ) : (
                  <Link to="/signup" className="valuation-portal__btn valuation-portal__btn--upgrade">
                    Create Free Account
                  </Link>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ValuationPortalScreen;
